import { StaticImage } from 'gatsby-plugin-image';
import { darken, lighten, rgba } from 'polished';
import styled from 'styled-components';

import { generateFluidPxScale } from '@/components/blog-2/utils';
import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { config } from '@/components/home-2025/config';
import { styles as headingStyles } from '@/components/v2/UtilityCss/Heading';
import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { ReactComponent as ChevronRight } from '@/images/icons/icon-chevron-right-medium.svg';
import { ReactComponent as IconMagic } from '@/images/icons/icon-magic.svg';
import { ReactComponent as IconMultiplayer } from '@/images/icons/icon-multiplayer.svg';
import { ReactComponent as IconPointAndClick } from '@/images/icons/icon-point-and-click.svg';
import { ReactComponent as IconSql } from '@/images/icons/icon-sql.svg';
import { INTERNAL_LINKS } from '@/utils';

import { DotGridMask } from '../Textures';

const STATIC_IMG_PATH = '../../../../static/images/home-2025';

const PerlinNoise = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${config.workflows.notebooks.noise};
  background-size: 512px 512px;
  opacity: 0;
  transition: opacity;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  animation: noise 8s linear infinite;
  animation-play-state: paused;
  @keyframes noise {
    from {
      background-position: -512px -512px;
    }
    to {
      background-position: 0px 0px;
    }
  }
`;

const LinkIndicator = styled.div`
  position: relative;
  padding: 14px 20px 14px 26px;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  color: ${({ theme }) => theme.palette.fgLoud};
  border: 1px dashed ${({ theme }) => theme.palette.fg1100};
  box-shadow:
    0 2px 3px 0px rgba(0 0 0 / 0.12),
    0 4px 12px -2px rgba(0 0 0 / 0.8);
  border-radius: 1px;
  position: absolute;
  bottom: 24px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 18px;
  transform-origin: left bottom;

  transform: rotateX(-15deg) translateY(56px);
  opacity: 0;
  transition: opacity, transform;
  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);

  svg {
    color: ${({ theme }) => theme.palette.fg1200};
  }

  &::after {
    content: ' ';
    border-radius: inherit;
    position: absolute;
    inset: 0;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  }
`;

export const Cell = styled.div`
  --hover-transition-duration: 529.33ms;
  --hover-transition-timing-function: linear(
    0 0%,
    0.005892 1%,
    0.022227 2%,
    0.047151 3%,
    0.079006 4%,
    0.116321 5%,
    0.157797 6%,
    0.202293 7.000000000000001%,
    0.248818 8%,
    0.296516 9%,
    0.344652 10%,
    0.392606 11%,
    0.439857 12%,
    0.485977 13%,
    0.530617 14.000000000000002%,
    0.573504 15%,
    0.614426 16%,
    0.653228 17%,
    0.689804 18%,
    0.724091 19%,
    0.756062 20%,
    0.785721 21%,
    0.813097 22%,
    0.838242 23%,
    0.861223 24%,
    0.882123 25%,
    0.901035 26%,
    0.918061 27%,
    0.933305 28.000000000000004%,
    0.946879 28.999999999999996%,
    0.958894 30%,
    0.96946 31%,
    0.978689 32%,
    0.986687 33%,
    0.993561 34%,
    0.999411 35%,
    1.004333 36%,
    1.00842 37%,
    1.011758 38%,
    1.014427 39%,
    1.016506 40%,
    1.018063 41%,
    1.019165 42%,
    1.019871 43%,
    1.020237 44%,
    1.020313 45%,
    1.020144 46%,
    1.019771 47%,
    1.019232 48%,
    1.018558 49%,
    1.01778 50%,
    1.016922 51%,
    1.016007 52%,
    1.015055 53%,
    1.014083 54%,
    1.013105 55.00000000000001%,
    1.012133 56.00000000000001%,
    1.011177 56.99999999999999%,
    1.010246 57.99999999999999%,
    1.009346 59%,
    1.008483 60%,
    1.00766 61%,
    1.006882 62%,
    1.006148 63%,
    1.005462 64%,
    1.004822 65%,
    1.00423 66%,
    1.003683 67%,
    1.003182 68%,
    1.002725 69%,
    1.002309 70%,
    1.001933 71%,
    1.001595 72%,
    1.001293 73%,
    1.001024 74%,
    1.000786 75%,
    1.000578 76%,
    1.000396 77%,
    1.000238 78%,
    1.000103 79%,
    0.999989 80%
  );

  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  border: 1px solid ${rgba(config.workflows.notebooks.color, 0.2)};
  transition: border transform;
  box-shadow:
    1px 2px 4px rgba(0, 0, 0, 0.2),
    4px 6px 8px rgba(0, 0, 0, 0.17),
    10px 14px 10px rgba(0, 0, 0, 0.1),
    18px 25px 12px rgba(0, 0, 0, 0.03),
    28px 39px 13px rgba(0, 0, 0, 0);

  transition-duration: var(--hover-transition-duration);
  transition-timing-function: var(--hover-transition-timing-function);
  perspective: 800px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    max-width: 500px;
    margin: 0 auto;
  }

  [data-gatsby-image-wrapper] {
    transition-duration: var(--hover-transition-duration);
    transition-timing-function: var(--hover-transition-timing-function);
    transform-origin: center bottom;
  }

  &[data-hero-cell] {
    grid-column: 1 / -1;
    flex-direction: row;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      grid-column: auto / span 1;
      flex-direction: column;
      align-self: flex-start;
    }

    ${LinkIndicator} {
      bottom: ${generateFluidPxScale(16, 32)};
      left: ${generateFluidPxScale(16, 40)};
      right: auto;
    }
  }

  &::before {
    border-radius: inherit;
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }

  &:has(*:focus-visible) {
    transform: translateY(-4px);
    border: 1px solid ${rgba(config.workflows.notebooks.color, 0.32)};
    outline: var(--app-focus-outline);
    outline-offset: var(--app-focus-outline-offset);

    ${PerlinNoise} {
      opacity: 0.64;
      animation-play-state: running;
    }
    ${LinkIndicator} {
      transform: none;
      opacity: 1;
    }

    [data-gatsby-image-wrapper] {
      transform: scale(0.986);
    }
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      border: 1px solid ${rgba(config.workflows.notebooks.color, 0.32)};

      ${PerlinNoise} {
        opacity: 0.64;
        animation-play-state: running;
      }
      ${LinkIndicator} {
        transform: none;
        opacity: 1;
      }

      [data-gatsby-image-wrapper] {
        transform: scale(0.986);
      }
    }
  }
`;

const Info = styled.div`
  margin-top: -8px;
  padding: ${generateFluidPxScale(32, 40)};
  min-width: 44ch;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    min-width: auto;
    padding: 32px ${generateFluidPxScale(24, 52)} !important;
  }

  &:not(&[data-hero-cell]) {
    min-width: auto;
    padding-bottom: ${generateFluidPxScale(16, 24)};
  }
`;

const Icon = styled.div`
  color: ${config.workflows.notebooks.color};
  margin-bottom: 8px;
`;

const Title = styled.h3`
  margin-bottom: 8px;
  text-wrap: balance;
`;

const Description = styled.p`
  max-width: 22rem;
`;

const Illustration = styled.div`
  position: relative;
  place-self: stretch;
  display: flex;
  flex-direction: column;
  margin: 8px;
  border: 1px solid ${({ theme }) => lighten(0.12, theme.palette.fg100)};
  border-radius: 6px;
  overflow: hidden;

  img {
    align-self: flex-end;
    display: block;
    width: 100%;
    filter: brightness(0.96);
  }

  &:not(&[data-hero-cell]) {
    margin-top: auto;
  }

  &[data-hero-cell] {
    padding: 32px 16px 0 32px;
  }
`;

const Target = styled.span`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const PlaceholderBg = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.palette.fgA700};
`;

export function PolyglotCell() {
  return (
    <Cell data-hero-cell>
      <Info data-hero-cell>
        <Icon className={textStyles.annotation}>
          <IconSql />
        </Icon>
        <LinkWithQuery to={INTERNAL_LINKS.Notebooks}>
          <Target />
          <Title className={headingStyles.h4}>
            SQL and Python, living together in harmony
          </Title>
        </LinkWithQuery>
        <Description className={textStyles.m}>
          Go back and forth between queries and scripts, without jumping between
          tools.
        </Description>
      </Info>
      <Illustration data-hero-cell>
        <DotGridMask>
          <PlaceholderBg />
          <PerlinNoise />
        </DotGridMask>
        <StaticImage
          alt="polyglot workflows illustration"
          placeholder="blurred"
          sizes="(min-width: 1220px) 1200px, (min-width: 800px) 80vw, 130vw"
          src={`${STATIC_IMG_PATH}/notebooks-polyglot.png`}
        />
      </Illustration>
      <LinkIndicator className={textStyles.base}>
        Discover notebooks <ChevronRight />
      </LinkIndicator>
    </Cell>
  );
}

export function NoCodeCell() {
  return (
    <Cell>
      <Info>
        <Icon className={textStyles.annotation}>
          <IconPointAndClick />
        </Icon>
        <LinkWithQuery to={INTERNAL_LINKS.Explore}>
          <Target />
          <Title className={headingStyles.h4}>
            Explore with point-and-click no-code tools
          </Title>
        </LinkWithQuery>
        <Description className={textStyles.m}>
          Hex makes it easy to filter, pivot, and pass results between steps
          without writing code.
        </Description>
      </Info>
      <Illustration>
        <DotGridMask>
          <PlaceholderBg />
          <PerlinNoise />
        </DotGridMask>
        <StaticImage
          alt="No code illustration"
          placeholder="blurred"
          sizes="(min-width: 800px) 33vw, 100vw"
          src={`${STATIC_IMG_PATH}/notebooks-no-code.png`}
          style={{ transformOrigin: 'right bottom' }}
        />
      </Illustration>
      <LinkIndicator className={textStyles.base}>
        Discover no-code <ChevronRight />
      </LinkIndicator>
    </Cell>
  );
}

export function MultiplayerCell() {
  return (
    <Cell>
      <Info>
        <Icon>
          <IconMultiplayer />
        </Icon>
        <LinkWithQuery to={INTERNAL_LINKS.ForTeams}>
          <Target />
          <Title className={headingStyles.h4}>Make data a team sport</Title>
        </LinkWithQuery>
        <Description className={textStyles.m}>
          Real-time collaboration, commenting, and version control
        </Description>
      </Info>
      <Illustration>
        <DotGridMask>
          <PlaceholderBg />
          <PerlinNoise />
        </DotGridMask>
        <StaticImage
          alt="Multiplayer illustration"
          placeholder="blurred"
          sizes="(min-width: 800px) 33vw, 100vw"
          src={`${STATIC_IMG_PATH}/notebooks-multiplayer.png`}
          style={{ transformOrigin: 'right bottom' }}
        />
      </Illustration>
      <LinkIndicator className={textStyles.base}>
        Discover multiplayer <ChevronRight />
      </LinkIndicator>
    </Cell>
  );
}

export function AiCell() {
  return (
    <Cell>
      <Info>
        <Icon>
          <IconMagic />
        </Icon>
        <LinkWithQuery to={INTERNAL_LINKS.Magic}>
          <Target />
          <Title className={headingStyles.h4}>
            Magical AI, just a prompt away
          </Title>
        </LinkWithQuery>
        <Description className={textStyles.m}>
          Use Hex’s integrated AI tools to get an intelligent assist,
          wherever you are.
        </Description>
      </Info>
      <Illustration>
        <DotGridMask>
          <PlaceholderBg />
          <PerlinNoise />
        </DotGridMask>
        <StaticImage
          alt="Magic AI Illustration"
          placeholder="blurred"
          sizes="(min-width: 800px) 33vw, 100vw"
          src={`${STATIC_IMG_PATH}/notebooks-magic.png`}
        />
      </Illustration>
      <LinkIndicator className={textStyles.base}>
        Discover AI <ChevronRight />
      </LinkIndicator>
    </Cell>
  );
}
